import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface ReportSectionProps {
  title: string;
  subtitle?: string;
  /** Render content only after the section is first expanded (lazy drill-down). */
  collapsible?: boolean;
  defaultOpen?: boolean;
  count?: number;
  children: ReactNode;
}

/**
 * A titled card section. When `collapsible`, children are mounted lazily on
 * first open — used for the large drill-down datasets so they don't render
 * (or compute) until the user asks for them.
 */
export function ReportSection({
  title,
  subtitle,
  collapsible = false,
  defaultOpen = true,
  count,
  children,
}: ReportSectionProps) {
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);
  const [hasOpened, setHasOpened] = useState(open);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) setHasOpened(true);
      return next;
    });
  };

  return (
    <section className="adv-card adv-card--featured">
      <header
        className={`flex items-center justify-between px-5 py-4 ${collapsible ? 'cursor-pointer select-none hover:bg-adv-sunken rounded-adv-lg transition-colors' : ''}`}
        onClick={collapsible ? toggle : undefined}
      >
        <div className="flex items-center gap-2 min-w-0">
          {collapsible && (
            <span className="text-adv-tertiary">
              {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </span>
          )}
          <h2 className="adv-section-title truncate">{title}</h2>
          {count !== undefined && (
            <span className="adv-tag adv-tag--default adv-num">
              {count}
            </span>
          )}
        </div>
        {subtitle && (
          <span className="text-adv-body-s text-adv-tertiary flex-shrink-0 ml-2">{subtitle}</span>
        )}
      </header>

      {open && (
        <div className="px-5 pb-5">
          {/* Lazy: only render children once the section has been opened */}
          {(!collapsible || hasOpened) && children}
        </div>
      )}
    </section>
  );
}
